import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogImage?: string;
  schema?: object | object[];
}

const DEFAULT_IMAGE =
  'https://public.readdy.ai/ai/img_res/ed58018d-bb73-4bb8-8590-7096e85d7af4.png';

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage = DEFAULT_IMAGE,
  schema,
}: SEOHeadProps) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || '';
  const canonicalUrl = `${siteUrl}${canonicalPath}`;
  const today = new Date().toISOString().split('T')[0];

  // Inject / cleanup JSON-LD script via useEffect (safe for SPA routing)
  useEffect(() => {
    if (!schema) return;

    const existingScripts = document.head.querySelectorAll(
      'script[data-seo-jsonld]'
    );
    existingScripts.forEach((s) => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(
      Array.isArray(schema) ? schema : [schema]
    );
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [schema]);

  return (
    <>
      {/* Primary meta — React 19 hoists these to <head> */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="last-modified" content={today} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </>
  );
}
